import { Box, Divider, Skeleton, Typography } from '@mui/material';
import { Product } from 'model/deals.model';
import { useEffect, useState } from 'react';
import { DealsService } from 'service/deals.service';
import zeplinColor from 'theme/Color';
import { Utilities } from 'utilities/utilities';
import { PRODUCT_FIELDS } from './data';

type PriceBookProductsProps = {
  products: Product[];
  pricebookId: string;
};

const PriceBookProducts = ({ products, pricebookId }: PriceBookProductsProps) => {
  const [pricebookName, setPricebookName] = useState<string>('N/A');
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    const fetchPricebookName = async () => {
      if (!pricebookId || pricebookId === 'none') return;

      try {
        setLoading(true);
        const res = await DealsService.getPricebooks();
        const name = Utilities.getMatchingRecordTypeKey(pricebookId, res);
        setPricebookName(name || 'N/A');
      } catch (error) {
        console.error('Failed to fetch pricebook name:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchPricebookName();
  }, [pricebookId]);

  if (loading) {
    return (
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          gap: '24px',
          pb: 2,
          flex: 1,
          overflow: 'scroll',
          pr: 1,
          width: '100%',
        }}
      >
        <Skeleton variant='text' width={150} height={32} />
        {Array.from({ length: 3 }).map((_, i) => (
          <Skeleton key={i} variant='text' height={24} width='80%' />
        ))}

        <Divider sx={{ my: 3 }} />

        <Skeleton variant='text' width={200} height={32} />
        {Array.from({ length: 6 }).map((_, i) => (
          <Skeleton key={i} variant='text' height={24} width='80%' />
        ))}
      </Box>
    );
  }

  if (!products || products.length === 0) {
    return (
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          justifyContent: 'center',
          alignItems: 'center',
          gap: '32px',
          py: 8,
          textAlign: 'center',
        }}
      >
        <img
          src='/assets/no-pricebook.svg'
          alt='No pricebook'
          style={{ width: '120px', height: '120px', opacity: 0.5 }}
        />
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
          <Typography variant='heading3' fontWeight='600'>
            No Products Added Yet
          </Typography>
          <Typography variant='body2' color='text.secondary'>
            Start by selecting a price book to add products
            <br /> and set pricing for this deal
          </Typography>
        </Box>
      </Box>
    );
  }

  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        gap: '24px',
        pb: 2,
        flex: 1,
        overflow: 'scroll',
        pr: 1,
        width: '100%',
      }}
    >
      <Box>
        <Box sx={{ marginBottom: '24px' }}>
          <Typography variant='heading3' fontWeight='bold' sx={{ color: zeplinColor.BasicBlack }}>
            Price Book
          </Typography>
        </Box>
        <Box sx={{ display: 'flex', flexDirection: 'row', gap: '8px', alignItems: 'center' }}>
          <Typography
            variant='body2'
            sx={{ width: 180, color: zeplinColor.Neutral8, flexShrink: 0 }}
          >
            Price book
          </Typography>
          <Typography variant='body2' sx={{ flexGrow: 1 }}>
            {pricebookName}
          </Typography>
        </Box>
      </Box>

      <Divider sx={{ my: 3 }} />

      <Box>
        <Box sx={{ marginBottom: '24px' }}>
          <Typography variant='heading3' fontWeight='bold' sx={{ color: zeplinColor.BasicBlack }}>
            Products ({products.length})
          </Typography>
        </Box>

        {products.map((product, index: number) => (
          <Box
            key={product.PricebookEntryId}
            sx={{ display: 'flex', flexDirection: 'column', gap: '24px', mb: 3 }}
          >
            {PRODUCT_FIELDS.map(({ key, label, render }) => {
              const value = product[key as keyof Product];
              const renderedValue = render ? render(value) : value || 'N/A';

              return (
                <Box key={key} sx={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
                  <Typography
                    variant='body2'
                    sx={{ width: 180, color: zeplinColor.Neutral8, flexShrink: 0 }}
                  >
                    {label}
                  </Typography>
                  <Typography variant='body2' sx={{ flexGrow: 1 }}>
                    {renderedValue}
                  </Typography>
                </Box>
              );
            })}
            {index < products.length - 1 && <Divider sx={{ mt: 2 }} />}
          </Box>
        ))}
      </Box>
    </Box>
  );
};

export default PriceBookProducts;
